import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UtensilsCrossed, Camera, CameraOff, Check, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { typeText } from "@/lib/ticketTypes";
import { useGuestLang, LangToggle } from "@/lib/guestI18n";

const FLAGS = {
  Bolivia: "🇧🇴", Colombia: "🇨🇴", Cuba: "🇨🇺", Ecuador: "🇪🇨", Guatemala: "🇬🇹",
  Mexico: "🇲🇽", Nicaragua: "🇳🇮", "Puerto Rico": "🇵🇷", Venezuela: "🇻🇪", Peru: "🇵🇪",
  "Dominican Republic": "🇩🇴", Brazil: "🇧🇷",
};

export default function PublicVoting() {
  const { lang, setLang, t } = useGuestLang();
  const [status, setStatus] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [votes, setVotes] = useState({});
  const [complete, setComplete] = useState(false);
  const [barcode, setBarcode] = useState("");
  const [cameraOn, setCameraOn] = useState(false);
  const [activeCat, setActiveCat] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState(false);
  const videoRef = useRef(null);
  const readerRef = useRef(null);

  useEffect(() => { api.get("/voting/status").then((r) => setStatus(r.data)).catch(() => {}); }, []);

  const verify = async (code) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/voting/verify/${encodeURIComponent(code.trim())}`);
      if (data.result === "OK") {
        setTicket(data.ticket);
        setVotes(data.votes);
        setComplete(data.complete);
      } else {
        toast.error(t(data.message));
      }
    } catch (e) { toast.error(t(formatApiError(e))); }
    finally { setBusy(false); }
  };

  const refresh = () => {
    if (ticket) api.get(`/voting/verify/${encodeURIComponent(ticket.barcode_token || barcode)}`).catch(() => {});
  };

  const submitVote = async () => {
    if (!selected || !activeCat || !ticket) return;
    setBusy(true);
    try {
      await api.post("/voting/vote", { barcode: barcode || ticket.barcode_token || "", category: activeCat, country: selected });
      const next = { ...votes, [activeCat]: selected };
      setVotes(next);
      const c = Object.values(next).filter(Boolean).length;
      setComplete(c >= 3);
      setActiveCat(null); setSelected(null);
      toast.success(t("Vote submitted"));
    } catch (e) {
      toast.error(t(formatApiError(e)));
      // reload votes so UI reflects DB state
      try {
        const { data } = await api.get(`/voting/verify/${encodeURIComponent(barcode)}`);
        if (data.result === "OK") { setVotes(data.votes); setComplete(data.complete); }
      } catch (_) {}
      setActiveCat(null); setSelected(null);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (!cameraOn) { try { readerRef.current?.reset(); } catch (e) {} readerRef.current = null; return; }
    let mounted = true;
    (async () => {
      try {
        const zx = await import("@zxing/library");
        const hints = new Map();
        hints.set(zx.DecodeHintType.POSSIBLE_FORMATS, [zx.BarcodeFormat.PDF_417]);
        hints.set(zx.DecodeHintType.TRY_HARDER, true);
        const reader = new zx.BrowserMultiFormatReader(hints);
        readerRef.current = reader;
        await reader.decodeFromVideoDevice(null, videoRef.current, (res) => {
          if (res && mounted) {
            const text = res.getText();
            setBarcode(text); setCameraOn(false); verify(text);
          }
        });
      } catch (e) { console.error(e); setCameraOn(false); toast.error(t("Camera not available. Please allow camera access or visit Registration.")); }
    })();
    return () => { mounted = false; try { readerRef.current?.reset(); } catch (e) {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraOn]);

  if (!status) return <div className="min-h-screen flex items-center justify-center bg-[#0B1120] text-slate-400">{t("Loading…")}</div>;

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100 relative overflow-hidden">
      <LangToggle lang={lang} setLang={setLang} />
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-orange-500/20 blur-3xl" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-rose-600/20 blur-3xl" />

      <div className="relative z-10 max-w-2xl mx-auto px-4 pt-14 pb-8">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-rose-500 flex items-center justify-center shadow-2xl shadow-orange-500/30 mb-3">
            <UtensilsCrossed className="text-white" size={26} />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">FOOD BAZAAR</h1>
          <h2 className="font-display text-xl font-bold text-orange-400 tracking-tight -mt-1">{t("VOTE FOR YOUR FAVORITES")}</h2>
        </div>

        {status.status === "CLOSED" ? (
          <div className="bg-[#131D33] border-2 border-rose-500/40 rounded-2xl p-8 text-center">
            <div className="font-display text-3xl font-black text-rose-300 tracking-tight">{t("VOTING IS NOW CLOSED")}</div>
            <p className="text-slate-400 mt-2">{t("Thank you for participating in Food Bazaar.")}</p>
          </div>
        ) : !ticket ? (
          <div className="bg-[#131D33] border border-slate-800 rounded-2xl p-6" data-testid="verify-panel">
            <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold mb-1">{t("Verify your ticket")}</div>
            <h3 className="font-display text-2xl font-black tracking-tight">{t("Scan your Food Bazaar ticket barcode to vote")}</h3>

            <div className="mt-4">
              <Button onClick={() => setCameraOn((v) => !v)} className="w-full h-12 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold" data-testid="vote-camera-toggle">
                {cameraOn ? <CameraOff size={16} className="mr-2" /> : <Camera size={16} className="mr-2" />}
                {cameraOn ? t("Stop Camera") : t("Scan Ticket Barcode")}
              </Button>
              {!manual ? (
                <Button variant="outline" onClick={() => { setManual(true); setCameraOn(false); }} className="mt-3 w-full h-12 bg-slate-900 border-slate-700 text-slate-200" data-testid="vote-manual-btn">{t("Enter Ticket Number Manually")}</Button>
              ) : (
                <div className="mt-4">
                  <label className="text-xs tracking-widest uppercase text-slate-400 font-bold">{t("Ticket number")}</label>
                  <div className="mt-2 flex gap-2">
                    <Input autoFocus value={barcode} onChange={(e) => setBarcode(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && verify(barcode)}
                      placeholder={t("e.g. REG-02K4M9")} className="h-14 text-lg font-mono-fb bg-[#0B1120] border-slate-700 text-white"
                      data-testid="verify-input" />
                    <Button onClick={() => verify(barcode)} disabled={busy || !barcode}
                      className="h-14 px-6 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold rounded-xl"
                      data-testid="verify-submit"><ScanLine size={18} className="mr-1" /> {t("Verify")}</Button>
                  </div>
                </div>
              )}
              {cameraOn && (
                <div className="mt-3 relative rounded-xl overflow-hidden border-2 border-orange-500/50 bg-black" data-testid="vote-camera">
                  <video ref={videoRef} className="w-full h-64 object-cover" muted playsInline autoPlay />
                  <div className="absolute inset-x-6 top-1/2 -translate-y-1/2 h-20 border-2 border-dashed border-orange-400/80 rounded-lg pointer-events-none" />
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="bg-[#131D33] border border-slate-800 rounded-2xl p-4 mb-4 text-center">
              <div className="text-xs tracking-widest uppercase text-slate-400 font-bold">{t("Voting as")}</div>
              <div className="font-mono-fb text-orange-300 text-lg">{ticket.ticket_id} · <span className={typeText(ticket.ticket_type)}>{ticket.ticket_type}</span></div>
              {ticket.guest_name && <div className="text-slate-300 text-sm">{ticket.guest_name}</div>}
            </div>

            {complete && (
              <div className="bg-emerald-950 border-2 border-emerald-500 rounded-2xl p-6 mb-4 text-center fb-pop" data-testid="all-complete">
                <div className="font-display text-3xl font-black text-emerald-300 tracking-tight">{t("THANK YOU FOR VOTING!")}</div>
                <div className="text-slate-300 mt-1 text-sm">{t("Your Food Bazaar votes have been submitted.")}</div>
              </div>
            )}

            <div className="space-y-4">
              {status.categories.map((c) => {
                const voted = votes[c.key];
                return (
                  <div key={c.key} className="bg-[#131D33] border border-slate-800 rounded-2xl p-5" data-testid={`cat-${c.key}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-display text-xl font-black tracking-tight">{t(c.label)}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{t(c.subtitle)}</div>
                      </div>
                      {voted ? (
                        <div className="px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-xs font-bold tracking-widest uppercase flex items-center gap-1"><Check size={12} /> {t("Voted")}</div>
                      ) : (
                        <div className="shrink-0 whitespace-nowrap px-3 py-1.5 rounded-full bg-orange-500/20 text-orange-300 border border-orange-500/40 text-xs font-bold tracking-widest uppercase">{t("Vote Now")}</div>
                      )}
                    </div>
                    {voted ? (
                      <div className="mt-3 text-slate-200"><span className="mr-2 text-2xl">{FLAGS[voted] || "🏳️"}</span><b>{voted}</b></div>
                    ) : (
                      <Button
                        onClick={() => { setActiveCat(c.key); setSelected(null); }}
                        className="mt-3 w-full h-12 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold"
                        data-testid={`open-${c.key}`}
                      >{t("VOTE NOW")}</Button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <Dialog open={!!activeCat} onOpenChange={(v) => !v && (setActiveCat(null), setSelected(null))}>
        <DialogContent className="bg-[#131D33] border-slate-700 text-white max-w-lg" data-testid="vote-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tight">
              {t(status?.categories?.find((c) => c.key === activeCat)?.label)}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 max-h-[55vh] overflow-y-auto">
            {status?.countries?.map((country) => (
              <button key={country} onClick={() => setSelected(country)} data-testid={`country-${country.replace(/ /g,'-')}`}
                className={`rounded-xl p-3 border text-left transition-all ${selected === country ? "bg-orange-500/20 border-orange-500 scale-[1.02]" : "bg-slate-900/70 border-slate-800 hover:bg-slate-800"}`}>
                <div className="text-2xl">{FLAGS[country] || "🏳️"}</div>
                <div className="font-display text-lg font-bold tracking-tight mt-1">{country}</div>
              </button>
            ))}
          </div>
          {selected && (
            <div className="rounded-xl bg-[#0B1120] border border-orange-500/30 p-3 text-sm">
              {t("You selected:")} <b className="text-orange-300">{selected}</b>
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setActiveCat(null); setSelected(null); }} className="flex-1 bg-slate-800 border-slate-700 text-slate-200" data-testid="vote-cancel">{t("GO BACK")}</Button>
            <Button onClick={submitVote} disabled={!selected || busy} className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold" data-testid="vote-confirm">{t("CONFIRM VOTE")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
